import { GoogleAnalyticsIds } from 'shared/types/cart/GoogleAnalyticsIds';
import { GA_MEASUREMENT_ID } from 'helpers/constants/googleAnalytics';

// The `_ga_` prefix stays case-sensitive — `/i` would also match `_GA_`, a different cookie — but
// the stream id itself is widened, so a non-standard casing still resolves.
const GA_SESSION_COOKIE_PATTERN = /(?:^|;\s*)(_ga_[A-Za-z0-9]+)\s*=/g;

// Upper-cased before validating: the cookie name is case-sensitive, so a mis-cased env var would
// otherwise build a name that never matches and suppress the fallback scan below.
const resolveStreamId = (measurementId?: string): string | null => {
  const streamId = measurementId?.trim().replace(/^G-/i, '').toUpperCase();

  return streamId && /^[A-Z0-9]+$/.test(streamId) ? streamId : null;
};

// Not helpers/utils/cookies.getCookie: its pattern is unanchored on the left, so `_ga` also matches
// a cookie named `padi_ga`.
const readCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '\\s*=\\s*([^;]*)'));

  return match ? match[1] : null;
};

// `GA1.1.<random>.<timestamp>` -> `<random>.<timestamp>`
export const parseGaClientId = (value?: string | null): string | null => {
  if (!value) return null;

  const parts = value.split('.');

  return parts.length < 4 ? null : parts.slice(-2).join('.');
};

// `GS1.1.<sessionId>.<n>...` or `GS2.1.s<sessionId>$o<n>$...`
export const parseGaSessionId = (value?: string | null): string | null => {
  if (!value) return null;

  if (value.startsWith('GS2.')) {
    const token = value
      .split('.')
      .slice(2)
      .join('.')
      .split('$')
      .find((part) => part.startsWith('s'));

    return token?.slice(1) || null;
  }

  if (value.startsWith('GS1.')) {
    return value.split('.')[2] || null;
  }

  return null;
};

// Several GA4 streams write `_ga_*` on padi.com, so the fallback scan refuses to guess between them.
const resolveSessionCookieName = (): string | null => {
  const streamId = resolveStreamId(GA_MEASUREMENT_ID);
  if (streamId) return `_ga_${streamId}`;

  const names = [...new Set([...document.cookie.matchAll(GA_SESSION_COOKIE_PATTERN)].map((match) => match[1]))];

  return names.length === 1 ? names[0] : null;
};

export const getGaClientId = (): string | null => {
  if (typeof document === 'undefined') return null;

  try {
    return parseGaClientId(readCookie('_ga'));
  } catch {
    return null;
  }
};

export const getGaSessionId = (): string | null => {
  if (typeof document === 'undefined') return null;

  try {
    const cookieName = resolveSessionCookieName();

    return cookieName ? parseGaSessionId(readCookie(cookieName)) : null;
  } catch {
    return null;
  }
};

export const analyticsConsentGranted = (): boolean => {
  if (typeof document === 'undefined') return false;

  try {
    const blocklist = readCookie('cmapi_gtm_bl');

    return blocklist === null ? false : !blocklist.split('-').includes('ga');
  } catch {
    return false;
  }
};

export const getGoogleAnalyticsIds = (): GoogleAnalyticsIds => {
  if (!analyticsConsentGranted()) return {};

  // The two ids come from separate cookies, so neither gates the other: a stream cookie that
  // survived without `_ga` still yields a session id worth attributing.
  const gaClientId = getGaClientId();
  const gaSessionId = getGaSessionId();

  return {
    ...(gaClientId ? { gaClientId } : {}),
    ...(gaSessionId ? { gaSessionId } : {}),
  };
};

// Why an id is or is not captured, as low-cardinality flags for logging. Deliberately carries no
// identifier values, so it is safe to send to Datadog where the ids themselves are not.
//
// `analyticsConsentGranted` fails closed, and TrustArc writes `cmapi_gtm_bl` only once the visitor
// records a choice — so a visitor who has not yet answered the consent banner captures nothing even
// though usable `_ga` cookies may exist. UAT confirmed that behaviour but could not size it, and the
// same blind spot covers any region whose TrustArc notice does not set that cookie at all. Counting
// `outcome` answers both from real traffic.
export type GoogleAnalyticsCaptureOutcome =
  | 'captured' // both ids sent
  | 'partial' // one id only — `hasClientId`/`hasSessionId` say which
  | 'no-consent-record' // `cmapi_gtm_bl` absent: banner unanswered, or a notice that never sets it
  | 'consent-denied' // visitor declined analytics
  | 'no-ga-cookies' // consent granted but GA4 wrote nothing (ad blocker, first load)
  | 'unknown'; // evaluated without a document

export type GoogleAnalyticsCaptureState = {
  consentRecorded: boolean;
  analyticsAllowed: boolean;
  hasClientId: boolean;
  hasSessionId: boolean;
  outcome: GoogleAnalyticsCaptureOutcome;
};

export const getGoogleAnalyticsCaptureState = (): GoogleAnalyticsCaptureState => {
  if (typeof document === 'undefined') {
    return {
      consentRecorded: false,
      analyticsAllowed: false,
      hasClientId: false,
      hasSessionId: false,
      outcome: 'unknown',
    };
  }

  let consentRecorded = false;
  try {
    consentRecorded = readCookie('cmapi_gtm_bl') !== null;
  } catch {
    consentRecorded = false;
  }

  const analyticsAllowed = analyticsConsentGranted();
  const ids = getGoogleAnalyticsIds();
  const hasClientId = Boolean(ids.gaClientId);
  const hasSessionId = Boolean(ids.gaSessionId);

  const outcome: GoogleAnalyticsCaptureOutcome = !consentRecorded
    ? 'no-consent-record'
    : !analyticsAllowed
      ? 'consent-denied'
      : !hasClientId && !hasSessionId
        ? 'no-ga-cookies'
        : hasClientId && hasSessionId
          ? 'captured'
          : 'partial';

  return { consentRecorded, analyticsAllowed, hasClientId, hasSessionId, outcome };
};
